import { colorsPalettes } from "@similarweb/styles";
import _ from "lodash";
import numeral from "numeral";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import monthlyIntervalConfig from "pro-features/components/Chart/src/configs/granularity/monthlyIntervalConfig";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import xAxisCategoryConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisCategoryConfig";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import yAxisGridLineConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisGridLineConfig";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";
import noZoomConfig from "pro-features/components/Chart/src/configs/zoom/noZoomConfig";
import noAnimationConfig from "pro-features/components/Chart/src/configs/animation/noAnimationConfig";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";

const columnContainerStyle = `width: 416px;box-sizing: border-box;padding: 0 8px; color: ${colorsPalettes.carbon[200]}`;
const rowStyle = `width: 100%;display: table;table-layout: fixed;border-collapse: collapse;margin: 0;`;
const headerRowStyle = `${rowStyle}margin: 2px 0;border-bottom: 1px solid ${colorsPalettes.carbon[200]}`;
const cellStyle = `display: table-cell;vertical-align: middle;text-align: right;`;
const columnCellStyle = `${cellStyle}width: 70px;`;
const columnLegendCellStyle = `${cellStyle}width: 130px;`;

const getTableBarHeaderRowMarkup = (point1, point2, durations) => {
    const point1ListItem = point1
        ? `<li style="${columnLegendCellStyle} color: ${point1.color}">${durations[0]}</li>`
        : ``;
    const point2ListItem = point2
        ? `<li style="${columnLegendCellStyle} color: ${point2.color}">${durations[1]}</li>`
        : ``;

    return `
        <ul style="${headerRowStyle}">
            <li style="${columnCellStyle}"></li>
            ${point1ListItem}
            ${point2ListItem}
            <li style="${columnCellStyle} color: ${colorsPalettes.midnight[400]}">Change</li>
        </ul>
    `;
};

const getTableRowMarkup = (yAxisFormatter, point1, point2, type = "line", metric = null) => {
    const change = Array.isArray(point1.point.change) ? point1.point.change[0] : point1.point.change;
    const changeColor = _.isNumber(change)
        ? change > 0
            ? colorsPalettes.green.s100
            : colorsPalettes.red.s100
        : colorsPalettes.carbon[200];

    // Render point values only if we have data for them
    const point1ListItem = point1
        ? ` <li style="${columnLegendCellStyle}">${yAxisFormatter({
              value: point1.y,
              confidence: point1?.point?.confidence,
          })}</li>`
        : ``;
    const point2ListItem = point2
        ? `<li style="${columnLegendCellStyle}">${yAxisFormatter({
              value: point2.y,
              confidence: point2?.point?.confidence,
          })}</li>`
        : ``;

    // Render change value only if we have data for both points, and change value.
    const changeFormatted = change && point1 && point2 ? numeral(change).format("0[.]00%") : "-";
    const changeListItem = `<li style="${cellStyle} color: ${changeColor}">${changeFormatted}</li>`;

    return `
        <ul style="${rowStyle}">
            <li>
                <span style="${"width: 70px;"} display: table-cell;vertical-align: middle;max-width: 80px;overflow: hidden;text-overflow: ellipsis;" title="${
        point1.series.name
    }">
                    ${point1.series.name}
                </span>
            </li>
            ${point1ListItem}
            ${point2ListItem}
            ${changeListItem}
        </ul>
    `;
};

const defaultTooltip = function (yAxisFormatter, type, durations, metric) {
    const point1 = this.points[0];
    const point2 = this.points[1];
    return `<div style="${columnContainerStyle}">
            ${getTableBarHeaderRowMarkup(point1, point2, durations)}
            ${getTableRowMarkup(yAxisFormatter, point1, point2, type, metric)}
        </div>`;
};

export default ({ type, yAxisFormatter, xAxisFormatter, categoryXSeries, legendDurations, height, metric }) =>
    combineConfigs({ type, yAxisFormatter, xAxisFormatter }, [
        monthlyIntervalConfig,
        yAxisLabelsConfig,
        categoryXSeries ? xAxisCategoryConfig : null,
        xAxisLabelsConfig,
        noZoomConfig,
        yAxisGridLineConfig,
        noLegendConfig,
        noAnimationConfig,
        tooltipContainerConfig,
        {
            tooltip: {
                shared: true,
                useHTML: true,
                formatter() {
                    return defaultTooltip.call(this, yAxisFormatter, type, legendDurations, metric);
                },
            },
            chart: {
                height,
            },
            xAxis: {
                crosshair: true,
                labels: {
                    style: {
                        fontSize: "14px",
                        textAlign: "center",
                        fontFamily: '"Roboto", sans-serif',
                    },
                },
            },
        },
    ]);
